// ============================================================================
// FSA DOCUMENT OUTPUT SETTINGS (ADR-0003) — Phase 1
// ============================================================================
// Two-panel editor (group sidebar + settings rows), mirroring the Configuration
// Settings screen. Edits the tenant default Document Output Profile: header
// content, page layout, font size, and footer. Signature wording is template /
// entity-driven and intentionally NOT configured here.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaDocumentOutputProfileService } from 'api/services/fsa/fsaDocumentOutputProfileService';
import {
  COLOR_MODE_LABELS,
  DEFAULT_OUTPUT_CONFIG,
  FONT_SCALE_LABELS,
  type ColorMode,
  type DocumentOutputConfig,
  type DocumentOutputProfile,
  type FontScale,
  type HeaderAlign,
  type HeaderFieldConfig,
  type PageNumberPosition,
  type PageSizeKey,
  type RegistrationTypeOption,
} from 'api/types/fsa/documentOutput.types';

type GroupKey = 'header' | 'page' | 'footer';

const GROUPS: { key: GroupKey; label: string; icon: string; description: string }[] = [
  { key: 'header', label: 'Header Content', icon: 'list',     description: 'What appears at the top of every page of the financial statements.' },
  { key: 'page',   label: 'Page & Font',   icon: 'settings', description: 'Paper size, text size and print palette for the generated PDF.' },
  { key: 'footer', label: 'Footer',        icon: 'file',     description: 'Footer text and page numbering shown at the bottom of every page.' },
];

const PAGE_SIZES: { value: PageSizeKey; label: string }[] = [
  { value: 'A4', label: 'A4' }, { value: 'LEGAL', label: 'Legal' }, { value: 'LETTER', label: 'Letter' },
];
const FONT_SCALES: { value: FontScale; label: string }[] =
  (['SMALL', 'MEDIUM', 'LARGE', 'XLARGE'] as FontScale[]).map(s => ({ value: s, label: FONT_SCALE_LABELS[s] }));
const ALIGNS: { value: HeaderAlign; label: string }[] = [
  { value: 'LEFT', label: 'Left' }, { value: 'CENTER', label: 'Center' }, { value: 'RIGHT', label: 'Right' },
];
const POSITIONS: { value: PageNumberPosition; label: string }[] = [
  { value: 'LEFT', label: 'Left' }, { value: 'CENTER', label: 'Center' }, { value: 'RIGHT', label: 'Right' },
];
const COLOR_MODES: { value: ColorMode; label: string }[] =
  (['MONOCHROME', 'BRAND'] as ColorMode[]).map(m => ({ value: m, label: COLOR_MODE_LABELS[m] }));

interface FormState {
  align: HeaderAlign;
  showClientName: boolean;
  showAddress: boolean;
  showFinancialYear: boolean;
  regEnabled: Record<string, boolean>;
  pageSize: PageSizeKey;
  fontScale: FontScale;
  colorMode: ColorMode;
  footerText: string;
  showPageNumber: boolean;
  pageNumberPosition: PageNumberPosition;
}

const fieldEnabled = (fields: HeaderFieldConfig[], key: string) =>
  fields.some(f => f.key === key && f.enabled);

function configToForm(profile: DocumentOutputProfile | null, regTypes: RegistrationTypeOption[]): FormState {
  const cfg = profile?.config ?? DEFAULT_OUTPUT_CONFIG;
  const fields = cfg.header?.fields ?? [];
  const regEnabled: Record<string, boolean> = {};
  regTypes.forEach(rt => {
    regEnabled[rt.code] = fields.some(f => f.key === 'REGISTRATION' && f.registrationTypeCode === rt.code && f.enabled);
  });
  return {
    align: cfg.header?.align ?? 'CENTER',
    showClientName: fieldEnabled(fields, 'CLIENT_LEGAL_NAME'),
    showAddress: fieldEnabled(fields, 'ADDRESS'),
    showFinancialYear: fieldEnabled(fields, 'FINANCIAL_YEAR'),
    regEnabled,
    pageSize: cfg.page?.size ?? 'A4',
    fontScale: cfg.font?.scale ?? 'MEDIUM',
    // Profiles saved before Phase 1.5 have no `color` node — they fall back to
    // MONOCHROME, matching how the backend resolves them for the print.
    colorMode: cfg.color?.mode ?? 'MONOCHROME',
    footerText: cfg.footer?.customText ?? '',
    showPageNumber: cfg.footer?.pageNumber?.show ?? true,
    pageNumberPosition: cfg.footer?.pageNumber?.position ?? 'RIGHT',
  };
}

function formToConfig(form: FormState): DocumentOutputConfig {
  const fields: HeaderFieldConfig[] = [
    { key: 'CLIENT_LEGAL_NAME', enabled: form.showClientName },
    { key: 'ADDRESS', enabled: form.showAddress },
    { key: 'FINANCIAL_YEAR', enabled: form.showFinancialYear },
    ...Object.entries(form.regEnabled)
      .filter(([, on]) => on)
      .map(([code]) => ({ key: 'REGISTRATION' as const, registrationTypeCode: code, enabled: true })),
  ];
  return {
    version: 1,
    header: { align: form.align, fields },
    page: { size: form.pageSize },
    font: { scale: form.fontScale },
    footer: {
      customText: form.footerText.trim() ? form.footerText.trim() : null,
      pageNumber: { show: form.showPageNumber, position: form.pageNumberPosition },
    },
    color: { mode: form.colorMode },
  };
}

// ---- presentational helpers (reuse the global config-* CSS classes) --------

const SettingRow: React.FC<{ label: string; description?: string; children: React.ReactNode }> = ({ label, description, children }) => (
  <div className="config-setting-row">
    <div className="config-setting-info">
      <div className="config-setting-label"><span>{label}</span></div>
      {description && <div className="config-setting-description">{description}</div>}
    </div>
    <div className="config-setting-control">{children}</div>
  </div>
);

const ToggleSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <label className="config-toggle-switch">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} />
    <span className="config-toggle-slider" />
  </label>
);

const selectWrap = (node: React.ReactNode) => <div style={{ minWidth: 200 }}>{node}</div>;
const portal = typeof document !== 'undefined' ? document.body : null;

const FsaDocumentOutputSettingsScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<DocumentOutputProfile | null>(null);
  const [regTypes, setRegTypes] = useState<RegistrationTypeOption[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const snapshotRef = useRef('');
  const [activeGroup, setActiveGroup] = useState<GroupKey>('header');
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [def, types] = await Promise.all([
        fsaDocumentOutputProfileService.getDefault(),
        fsaDocumentOutputProfileService.getRegistrationTypes(),
      ]);
      setProfile(def);
      setRegTypes(types);
      const f = configToForm(def, types);
      setForm(f);
      snapshotRef.current = JSON.stringify(f);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load output settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirty = useMemo(() => form != null && snapshotRef.current !== '' && JSON.stringify(form) !== snapshotRef.current, [form]);
  const set = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm(prev => (prev ? { ...prev, [k]: v } : prev));
  }, []);

  const handleConfirmSave = useCallback(async () => {
    if (!form) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const config = formToConfig(form);
      const name = profile?.name?.trim() || 'Firm House Style';
      if (profile) {
        await fsaDocumentOutputProfileService.update(profile.id, {
          name, description: profile.description ?? null, isDefault: true, config, statusCode: 'ACTIVE', rowVersion: profile.rowVersion,
        });
      } else {
        await fsaDocumentOutputProfileService.create({ name, isDefault: true, config, statusCode: 'ACTIVE' });
      }
      notificationService.toast.success('Document output settings saved.');
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to save output settings.');
    } finally {
      setSaving(false);
    }
  }, [form, profile, load]);

  const handleDiscard = useCallback(() => {
    setForm(configToForm(profile, regTypes));
    setDiscardConfirmOpen(false);
  }, [profile, regTypes]);

  const group = GROUPS.find(g => g.key === activeGroup)!;

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Document Output Settings"
        description="Controls how FSA financial-statement PDFs are generated — applies to every engagement."
        icon={<IconSystem.settings size={18} />}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="config-settings-container">
          {/* Group sidebar */}
          <div className="config-group-sidebar">
            {GROUPS.map(g => {
              const Icon = (IconSystem as any)[g.icon];
              return (
                <div
                  key={g.key}
                  role="button"
                  tabIndex={0}
                  className={`config-group-item${activeGroup === g.key ? ' active' : ''}`}
                  onClick={() => setActiveGroup(g.key)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveGroup(g.key); } }}
                >
                  {Icon && <span className="config-group-icon"><Icon size={18} /></span>}
                  <span>{g.label}</span>
                </div>
              );
            })}
          </div>

          {/* Settings panel */}
          <div className="config-settings-panel">
            <div className="config-panel-header">
              <h2>{group.label}</h2>
              <p>{group.description}</p>
            </div>

            <div className="config-settings-list">
              {loading || !form ? (
                <div className="config-loading">Loading settings…</div>
              ) : activeGroup === 'header' ? (
                <>
                  <SettingRow label="Alignment" description="How the header lines are aligned on the page.">
                    {selectWrap(
                      <SyutSelect value={form.align} options={ALIGNS} isClearable={false} size="sm"
                        onChange={v => set('align', (v as HeaderAlign) || 'CENTER')}
                        menuPortalTarget={portal} menuPlacement="auto" />
                    )}
                  </SettingRow>
                  <SettingRow label="Client legal name" description="The registered legal name of the client.">
                    <ToggleSwitch checked={form.showClientName} onChange={v => set('showClientName', v)} />
                  </SettingRow>
                  <SettingRow label="Address" description="Client's registered address.">
                    <ToggleSwitch checked={form.showAddress} onChange={v => set('showAddress', v)} />
                  </SettingRow>
                  <SettingRow label="Financial year" description="e.g. “Financial Year 2025-26”.">
                    <ToggleSwitch checked={form.showFinancialYear} onChange={v => set('showFinancialYear', v)} />
                  </SettingRow>

                  {regTypes.length > 0 && (
                    <>
                      <div className="config-setting-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                        <div className="config-setting-info">
                          <div className="config-setting-label"><span>Registrations</span></div>
                          <div className="config-setting-description">
                            Statutory identifiers — each renders only when the client actually holds that registration.
                          </div>
                        </div>
                      </div>
                      {regTypes.map(rt => (
                        <SettingRow key={rt.code} label={rt.code} description={rt.name}>
                          <ToggleSwitch checked={!!form.regEnabled[rt.code]}
                            onChange={v => set('regEnabled', { ...form.regEnabled, [rt.code]: v })} />
                        </SettingRow>
                      ))}
                    </>
                  )}
                </>
              ) : activeGroup === 'page' ? (
                <>
                  <SettingRow label="Page Layout" description="Paper size for the generated PDF.">
                    {selectWrap(
                      <SyutSelect value={form.pageSize} options={PAGE_SIZES} isClearable={false} size="sm"
                        onChange={v => set('pageSize', (v as PageSizeKey) || 'A4')}
                        menuPortalTarget={portal} menuPlacement="auto" />
                    )}
                  </SettingRow>
                  <SettingRow label="Font Size" description="Overall text size — scales the whole document proportionally.">
                    {selectWrap(
                      <SyutSelect value={form.fontScale} options={FONT_SCALES} isClearable={false} size="sm"
                        onChange={v => set('fontScale', (v as FontScale) || 'MEDIUM')}
                        menuPortalTarget={portal} menuPlacement="auto" />
                    )}
                  </SettingRow>
                  <SettingRow
                    label="Print Colour"
                    description="Monochrome prints the signed statement in neutral greys — no tinted note cards, no coloured cross-references — so it stays legible on a black-and-white laser or photocopy. Brand colour keeps the on-screen blue treatment, better suited to drafts and internal review copies."
                  >
                    {selectWrap(
                      <SyutSelect value={form.colorMode} options={COLOR_MODES} isClearable={false} size="sm"
                        onChange={v => set('colorMode', (v as ColorMode) || 'MONOCHROME')}
                        menuPortalTarget={portal} menuPlacement="auto" />
                    )}
                  </SettingRow>
                </>
              ) : (
                <>
                  <SettingRow label="Custom Footer Text" description="Shown at the bottom-left. Leave blank to show “Annual Report YYYY-YY”.">
                    <input type="text" value={form.footerText} placeholder="(blank = Annual Report label)"
                      onChange={e => set('footerText', e.target.value)} style={{ minWidth: 260 }} />
                  </SettingRow>
                  <SettingRow label="Show page number" description="Display “Page X of Y” in the footer.">
                    <ToggleSwitch checked={form.showPageNumber} onChange={v => set('showPageNumber', v)} />
                  </SettingRow>
                  <SettingRow label="Page number position" description="Where the page number appears in the footer.">
                    {selectWrap(
                      <SyutSelect value={form.pageNumberPosition} options={POSITIONS} isClearable={false} size="sm"
                        isDisabled={!form.showPageNumber}
                        onChange={v => set('pageNumberPosition', (v as PageNumberPosition) || 'RIGHT')}
                        menuPortalTarget={portal} menuPlacement="auto" />
                    )}
                  </SettingRow>
                </>
              )}
            </div>

            {(isDirty || saving) && (
              <div className="config-footer-bar">
                <button type="button" className="btn btn-secondary" onClick={() => setDiscardConfirmOpen(true)} disabled={saving}>
                  Discard Changes
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)} disabled={saving || !isDirty}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Output Settings"
        message="Apply these document output settings? They affect every FSA engagement's PDF."
        confirmLabel="Save" cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmSave} onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes" message="Discard all unsaved changes?"
        confirmLabel="Discard" cancelLabel="Cancel" variant="danger"
        onConfirm={handleDiscard} onCancel={() => setDiscardConfirmOpen(false)}
      />
    </div>
  );
};

export default FsaDocumentOutputSettingsScreen;
